import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { useRecentCustomersStore, type RecentCustomer, type RecentCustomerType } from '../../stores/recent-customers-store';
import type { BrandFlags } from '../../stores/klanten-store';
import { supabase } from '../../lib/supabase';
import { formatRelativeTime } from '../../lib/format';
import { BRAND_COLORS } from '../../lib/order-constants';

interface RecentCreatedItem {
  id: string;
  type: RecentCustomerType;
  name: string;
  email: string;
  phone: string;
  city: string;
  postcode: string;
  createdDate: string;
  brandFlags?: BrandFlags;
}

interface RecentCreatedResponse {
  accounts: RecentCreatedItem[];
  leads: RecentCreatedItem[];
}

function toRouteType(t: RecentCustomerType): 'account' | 'lead' {
  return t === 'lead' ? 'lead' : 'account';
}

function BrandChips({ flags }: { flags?: BrandFlags }) {
  if (!flags) return null;
  const fallback = 'bg-slate-100 text-slate-600';
  const active: Array<[string, string]> = [];
  if (flags.kleda) active.push(['KLEDA', BRAND_COLORS.KLEDA ?? fallback]);
  if (flags.cinco) active.push(['CINCO', BRAND_COLORS.CINCO ?? fallback]);
  if (flags.sofasaver) active.push(['SOFASAVER', BRAND_COLORS.SOFASAVER ?? fallback]);
  if (flags.schoonhuisshop) active.push(['SHS', BRAND_COLORS.SCHOONHUISSHOP ?? fallback]);
  if (active.length === 0) return null;
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {active.map(([label, cls]) => (
        <span key={label} className={clsx('text-[10px] px-1.5 py-0.5 rounded font-medium', cls)}>
          {label}
        </span>
      ))}
    </div>
  );
}

function CustomerCard({
  entry,
  subtitle,
  onClick,
}: {
  entry: { id: string; type: RecentCustomerType; name: string; city: string; brandFlags?: BrandFlags };
  subtitle?: string;
  onClick: () => void;
}) {
  const isLead = entry.type === 'lead';
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-slate-50 transition-colors text-left"
    >
      <div
        className={clsx(
          'w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0',
          isLead ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'
        )}
      >
        <span className="text-sm font-semibold">{(entry.name.charAt(0) || '?').toUpperCase()}</span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-sm font-medium text-slate-900 truncate">{entry.name || '(Zonder naam)'}</span>
          <span
            className={clsx(
              'text-[10px] px-1.5 py-0.5 rounded font-medium uppercase tracking-wide flex-shrink-0',
              isLead ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'
            )}
          >
            {isLead ? 'Lead' : 'Klant'}
          </span>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          {entry.city && <span className="truncate">{entry.city}</span>}
          <BrandChips flags={entry.brandFlags} />
        </div>
        {subtitle && <div className="text-[11px] text-slate-400 mt-0.5">{subtitle}</div>}
      </div>
    </button>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col">
      <h2 className="px-3 pb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">{title}</h2>
      <div className="flex flex-col">{children}</div>
    </div>
  );
}

function EmptyRow({ text }: { text: string }) {
  return <p className="px-3 py-4 text-xs text-slate-400">{text}</p>;
}

function SkeletonRow() {
  return (
    <div className="flex items-center gap-3 px-3 py-2.5 animate-pulse">
      <div className="w-9 h-9 bg-slate-200 rounded-full" />
      <div className="flex-1 space-y-1.5">
        <div className="h-3 bg-slate-200 rounded w-32" />
        <div className="h-2.5 bg-slate-100 rounded w-48" />
      </div>
    </div>
  );
}

export function RecentCustomers() {
  const navigate = useNavigate();
  const recent = useRecentCustomersStore((s) => s.recent);
  const [created, setCreated] = useState<RecentCreatedResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;
        if (!token) {
          if (!cancelled) setLoading(false);
          return;
        }
        const res = await fetch('/api/customers/recent-created', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          if (!cancelled) setLoading(false);
          return;
        }
        const data: RecentCreatedResponse = await res.json();
        if (!cancelled) {
          setCreated(data);
          setLoading(false);
        }
      } catch (err) {
        console.error('[RecentCustomers] fetch error:', err);
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const open = (id: string, type: RecentCustomerType) => {
    navigate(`/klanten/${id}?type=${toRouteType(type)}`);
  };

  const newAccounts = created?.accounts ?? [];
  const newLeads = created?.leads ?? [];

  return (
    <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
      <Section title="Recent bezocht">
        {recent.length === 0 ? (
          <EmptyRow text="Nog geen recent bekeken klanten — open een klant om hier te verschijnen." />
        ) : (
          recent.map((r: RecentCustomer) => (
            <CustomerCard
              key={r.id}
              entry={r}
              subtitle={r.lastViewedAt ? formatRelativeTime(r.lastViewedAt) : undefined}
              onClick={() => open(r.id, r.type)}
            />
          ))
        )}
      </Section>

      <Section title="Recent aangemaakt (7 dagen)">
        {loading ? (
          <>
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
          </>
        ) : newAccounts.length === 0 && newLeads.length === 0 ? (
          <EmptyRow text="Geen nieuwe klanten of leads in de laatste 7 dagen." />
        ) : (
          <>
            {newAccounts.length > 0 && (
              <div className="flex flex-col">
                <h3 className="px-3 pt-1 pb-1 text-[11px] font-medium text-slate-400">Accounts</h3>
                {newAccounts.map((a) => (
                  <CustomerCard
                    key={a.id}
                    entry={a}
                    subtitle={a.createdDate ? formatRelativeTime(a.createdDate) : undefined}
                    onClick={() => open(a.id, a.type)}
                  />
                ))}
              </div>
            )}
            {newLeads.length > 0 && (
              <div className="flex flex-col">
                <h3 className="px-3 pt-2 pb-1 text-[11px] font-medium text-slate-400">Leads</h3>
                {newLeads.map((l) => (
                  <CustomerCard
                    key={l.id}
                    entry={l}
                    subtitle={l.createdDate ? formatRelativeTime(l.createdDate) : undefined}
                    onClick={() => open(l.id, l.type)}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </Section>
    </div>
  );
}
